import { useForm } from "@tanstack/react-form";
import {
	createFileRoute,
	stripSearchParams,
	useNavigate,
	useRouteContext,
	useSearch,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useId, useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { toast } from "sonner";
import z from "zod";
import { Icon } from "@/components/icon";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
} from "@/components/ui/drawer";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useIsMobile } from "@/hooks/useMobile";
import { MarketingIntegrationInfo } from "@/utils/marketing-integration-info";
import { PaymentProviderInfo } from "@/utils/payment-provider-info";
import type { integracoesCreateIntegrationTagMutation } from "./__generated__/integracoesCreateIntegrationTagMutation.graphql";
import type { integracoesCreateWhatsappIntegrationMutation } from "./__generated__/integracoesCreateWhatsappIntegrationMutation.graphql";
import type { integracoesQuery } from "./__generated__/integracoesQuery.graphql";
import type { integracoesRemoveTagsIntegrationMutation } from "./__generated__/integracoesRemoveTagsIntegrationMutation.graphql";
import type { integracoesRemoveWhatsappIntegrationMutation } from "./__generated__/integracoesRemoveWhatsappIntegrationMutation.graphql";

const defaultSearch = {
	view: "all",
} as const;

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/configuracoes/integracoes/",
)({
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			view: z.enum(["all", "marketing", "checkout"]).default("all"),
		}),
	),
	search: {
		middlewares: [stripSearchParams(defaultSearch)],
	},
});

function RouteComponent() {
	const { view } = useSearch({ from: Route.id });
	const navigate = useNavigate({ from: Route.fullPath });
	const { club } = useRouteContext({ from: "/_logged/c/$club" });
	const isMobile = useIsMobile();
	const _whatsappCheckboxId = useId();

	const [isGtmDrawerOpen, setIsGtmDrawerOpen] = useState(false);
	const [isGaDrawerOpen, setIsGaDrawerOpen] = useState(false);
	const [isMetaPixelDrawerOpen, setIsMetaPixelDrawerOpen] = useState(false);
	const [isWhatsappDrawerOpen, setIsWhatsappDrawerOpen] = useState(false);
	const [whatsappConfirmed, setWhatsappConfirmed] = useState(false);
	const [integrationToRemove, setIntegrationToRemove] = useState<{
		id: string;
		name: string;
		integrationType: "TagsIntegration" | "WhatsappIntegration";
	} | null>(null);

	const data = useLazyLoadQuery<integracoesQuery>(
		graphql`
    query integracoesQuery($slug: String!) {
      club(slug: $slug) {
        integrations {
          __typename
          id
          ... on PaymentIntegration {
              checkoutProvider: provider
              tested
            __typename
          }
          ... on TagsIntegration {
              tagProvider: provider
            __typename
          }
          ... on WhatsappIntegration {
            __typename
          }
        }
      }
    }
  `,
		{ slug: club.slug },
	);

	const [createIntegrationTag, isCreatingIntegration] =
		useMutation<integracoesCreateIntegrationTagMutation>(graphql`
			mutation integracoesCreateIntegrationTagMutation(
				$club: String!
				$input: CreateIntegrationTagInput!
			) {
				createIntegrationTag(club: $club, createIntegrationTagInput: $input) {
					id
					tagProvider: provider
					__typename
					club {
						id
						integrations {
							__typename
							id
							... on PaymentIntegration {
								checkoutProvider: provider
								tested
								__typename
							}
							... on TagsIntegration {
								tagProvider: provider
								__typename
							}
							... on WhatsappIntegration {
								__typename
							}
						}
					}
				}
			}
		`);

	const [createWhatsappIntegration, isCreatingWhatsappIntegration] =
		useMutation<integracoesCreateWhatsappIntegrationMutation>(graphql`
			mutation integracoesCreateWhatsappIntegrationMutation(
				$club: ID!
				$payload: CreateIntegrationInput!
			) {
				createWhatsappIntegration(club: $club, payload: $payload) {
					id
					__typename
					club {
						id
						integrations {
							__typename
							id
							... on PaymentIntegration {
								checkoutProvider: provider
								tested
								__typename
							}
							... on TagsIntegration {
								tagProvider: provider
								__typename
							}
							... on WhatsappIntegration {
								__typename
							}
						}
					}
				}
			}
		`);

	const [removeTagsIntegration, isRemovingTagsIntegration] =
		useMutation<integracoesRemoveTagsIntegrationMutation>(graphql`
			mutation integracoesRemoveTagsIntegrationMutation(
				$club: String!
				$id: ID!
			) {
				removeTagsIntegration(club: $club, id: $id) {
					id
					integrations {
						__typename
						id
						... on PaymentIntegration {
							checkoutProvider: provider
							tested
							__typename
						}
						... on TagsIntegration {
							tagProvider: provider
							__typename
						}
						... on WhatsappIntegration {
							__typename
						}
					}
				}
			}
		`);

	const [removeWhatsappIntegration, isRemovingWhatsappIntegration] =
		useMutation<integracoesRemoveWhatsappIntegrationMutation>(graphql`
			mutation integracoesRemoveWhatsappIntegrationMutation(
				$club: ID!
				$id: ID!
			) {
				removeWhatsappIntegration(club: $club, id: $id) {
					id
					integrations {
						__typename
						id
						... on PaymentIntegration {
							checkoutProvider: provider
							tested
							__typename
						}
						... on TagsIntegration {
							tagProvider: provider
							__typename
						}
						... on WhatsappIntegration {
							__typename
						}
					}
				}
			}
		`);

	const isRemovingIntegration =
		isRemovingTagsIntegration || isRemovingWhatsappIntegration;

	const installedIntegrations = data.club.integrations.filter(
		(it) => it.__typename,
	);

	const getInstalledIntegration = (id: string) => {
		switch (id) {
			case "FACEBOOK":
				return installedIntegrations.find(
					(it) =>
						it.__typename === "TagsIntegration" &&
						it.tagProvider === "FACEBOOK",
				);
			case "GOOGLE_ANALYTICS":
				return installedIntegrations.find(
					(it) =>
						it.__typename === "TagsIntegration" &&
						it.tagProvider === "GOOGLE_ANALYTICS",
				);
			case "GOOGLE_TAG_MANAGER":
				return installedIntegrations.find(
					(it) =>
						it.__typename === "TagsIntegration" &&
						it.tagProvider === "GOOGLE_TAG_MANAGER",
				);
			case "WHATSAPP":
				return installedIntegrations.find(
					(it) => it.__typename === "WhatsappIntegration",
				);
			case "HOTMART":
			case "EDUZZ":
			case "GURU":
			case "TMB":
			case "SCARF_PAY":
			case "HEROSPARK":
			case "STRIPE":
			case "GREENN":
			case "HUBLA":
			case "KIWIFY":
			case "TICTO":
			case "KIRVANO":
				return installedIntegrations.find(
					(it) =>
						it.__typename === "PaymentIntegration" &&
						it.checkoutProvider === id,
				);
			default:
				return undefined;
		}
	};

	const allIntegrations = [
		...(
			Object.keys(MarketingIntegrationInfo) as Array<
				keyof typeof MarketingIntegrationInfo
			>
		).map((it) => {
			const info = MarketingIntegrationInfo[it];
			const installed = getInstalledIntegration(it);
			return {
				...info,
				id: it,
				type: "MARKETING" as const,
				installed: !!installed,
				integrationId: installed?.id,
				integrationType: installed?.__typename as
					| "TagsIntegration"
					| "WhatsappIntegration"
					| undefined,
			};
		}),
		...(
			Object.keys(PaymentProviderInfo) as Array<
				keyof typeof PaymentProviderInfo
			>
		).map((it) => {
			const info = PaymentProviderInfo[it];
			const installed = getInstalledIntegration(it);
			return {
				...info,
				id: it,
				description: `Faça suas vendas pela ${info?.name} e disponibilize o conteúdo de forma automática`,
				type: "CHECKOUT" as const,
				installed: !!installed,
				integrationId: installed?.id,
				tested:
					installed?.__typename === "PaymentIntegration"
						? (installed.tested ?? undefined)
						: undefined,
				integrationType: undefined as
					| "TagsIntegration"
					| "WhatsappIntegration"
					| undefined,
			};
		}),
	];

	const gtmForm = useForm({
		defaultValues: {
			containerId: "",
		},
		validators: {
			onChange: z.object({
				containerId: z
					.string()
					.min(1, "O ID do container é obrigatório")
					.regex(/^GTM-/, "O ID do container deve começar com 'GTM-'"),
			}),
		},
		onSubmit: async ({ value }) => {
			return new Promise<void>((resolve, reject) => {
				createIntegrationTag({
					variables: {
						club: club.slug,
						input: {
							provider: "GOOGLE_TAG_MANAGER",
							code: value.containerId,
							active: true,
						},
					},
					onCompleted: (_response, errors) => {
						if (errors?.length) {
							const error = new Error(errors[0].message);
							toast.error(error.message);
							reject(error);
							return;
						}
						toast.success("Google Tag Manager configurado com sucesso!");
						setIsGtmDrawerOpen(false);
						gtmForm.reset();
						resolve();
					},
					onError: (error) => {
						toast.error(
							error.message || "Erro ao configurar Google Tag Manager",
						);
						reject(error);
					},
				});
			});
		},
	});

	const gaForm = useForm({
		defaultValues: {
			propertyId: "",
		},
		validators: {
			onChange: z.object({
				propertyId: z
					.string()
					.min(1, "O ID da propriedade é obrigatório")
					.regex(/^G-/, "O ID da propriedade deve começar com 'G-'"),
			}),
		},
		onSubmit: async ({ value }) => {
			return new Promise<void>((resolve, reject) => {
				createIntegrationTag({
					variables: {
						club: club.slug,
						input: {
							provider: "GOOGLE_ANALYTICS",
							code: value.propertyId,
							active: true,
						},
					},
					onCompleted: (_response, errors) => {
						if (errors?.length) {
							const error = new Error(errors[0].message);
							toast.error(error.message);
							reject(error);
							return;
						}
						toast.success("Google Analytics configurado com sucesso!");
						setIsGaDrawerOpen(false);
						gaForm.reset();
						resolve();
					},
					onError: (error) => {
						toast.error(error.message || "Erro ao configurar Google Analytics");
						reject(error);
					},
				});
			});
		},
	});

	const metaPixelForm = useForm({
		defaultValues: {
			pixelId: "",
		},
		validators: {
			onChange: z.object({
				pixelId: z.string().min(1, "O ID do Pixel é obrigatório"),
			}),
		},
		onSubmit: async ({ value }) => {
			return new Promise<void>((resolve, reject) => {
				createIntegrationTag({
					variables: {
						club: club.slug,
						input: {
							provider: "FACEBOOK",
							code: value.pixelId,
							active: true,
						},
					},
					onCompleted: (_response, errors) => {
						if (errors?.length) {
							const error = new Error(errors[0].message);
							toast.error(error.message);
							reject(error);
							return;
						}
						toast.success("Meta Pixel configurado com sucesso!");
						setIsMetaPixelDrawerOpen(false);
						metaPixelForm.reset();
						resolve();
					},
					onError: (error) => {
						toast.error(error.message || "Erro ao configurar Meta Pixel");
						reject(error);
					},
				});
			});
		},
	});

	const whatsappForm = useForm({
		defaultValues: {
			groupName: "",
		},
		validators: {
			onChange: z.object({
				groupName: z.string().min(1, "O nome do grupo é obrigatório"),
			}),
		},
		onSubmit: async ({ value }) => {
			return new Promise<void>((resolve, reject) => {
				createWhatsappIntegration({
					variables: {
						club: club.slug,
						payload: {
							name: value.groupName,
						},
					},
					onCompleted: (_response, errors) => {
						if (errors?.length) {
							const error = new Error(errors[0].message);
							toast.error(error.message);
							reject(error);
							return;
						}
						toast.success("Whatsapp configurado com sucesso!");
						setIsWhatsappDrawerOpen(false);
						setWhatsappConfirmed(false);
						whatsappForm.reset();
						resolve();
					},
					onError: (error) => {
						toast.error(error.message || "Erro ao configurar Whatsapp");
						reject(error);
					},
				});
			});
		},
	});

	function handleIntegrationClick(integrationId: string) {
		if (integrationId === "GOOGLE_TAG_MANAGER") {
			setIsGtmDrawerOpen(true);
		} else if (integrationId === "GOOGLE_ANALYTICS") {
			setIsGaDrawerOpen(true);
		} else if (integrationId === "FACEBOOK") {
			setIsMetaPixelDrawerOpen(true);
		} else if (integrationId === "WHATSAPP") {
			setIsWhatsappDrawerOpen(true);
		} else if (integrationId in PaymentProviderInfo) {
			const providerInfo =
				PaymentProviderInfo[integrationId as keyof typeof PaymentProviderInfo];
			navigate({
				to: "/c/$club/configuracoes/integracoes/checkout/$slug/novo",
				params: { club: club.slug, slug: providerInfo.slug },
			});
		}
	}

	function handleRemoveIntegration() {
		if (!integrationToRemove) return;

		const onCompleted = (
			_response: unknown,
			errors: ReadonlyArray<{ message: string }> | null,
		) => {
			if (errors?.length) {
				toast.error(errors[0].message);
				return;
			}
			toast.success("Integração removida com sucesso!");
			setIntegrationToRemove(null);
		};

		const onError = (error: Error) => {
			toast.error(error.message || "Erro ao remover integração");
		};

		if (integrationToRemove.integrationType === "WhatsappIntegration") {
			removeWhatsappIntegration({
				variables: {
					club: club.slug,
					id: integrationToRemove.id,
				},
				onCompleted,
				onError,
			});
		} else {
			removeTagsIntegration({
				variables: {
					club: club.slug,
					id: integrationToRemove.id,
				},
				onCompleted,
				onError,
			});
		}
	}

	function renderIntegration({
		id,
		name,
		logo,
		description,
		installed,
		integrationId,
		integrationType,
		tested,
		type,
	}: {
		id: string;
		name: string;
		logo: string;
		description: string;
		installed: boolean;
		integrationId?: string;
		integrationType?: "TagsIntegration" | "WhatsappIntegration";
		tested?: boolean;
		type: "MARKETING" | "CHECKOUT";
	}) {
		const canRemove =
			installed && integrationId && integrationType && type === "MARKETING";
		const canManage = installed && integrationId && type === "CHECKOUT";

		return (
			<div
				key={id}
				className="p-6 flex flex-col border rounded-md max-w-md w-full items-start gap-3"
			>
				<div className="flex flex-row items-center gap-3">
					<Avatar className="border-muted-foreground border">
						<AvatarImage src={logo} />
					</Avatar>

					<div className="flex flex-col">
						<div className="flex flex-row items-center justify-between">
							<span>{name}</span>
						</div>

						<span className="text-sm text-muted-foreground text-balance line-clamp-2">
							{description || `Sem descrição`}
						</span>
					</div>
				</div>

				<div className="flex flex-row gap-3 items-center">
					<div className="w-8" />

					{installed ? (
						<>
							{type === "CHECKOUT" && tested !== true ? (
								<Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20 hover:bg-yellow-500/10">
									<Icon name="TriangleAlert" className="size-3" />
									Ação Pendente
								</Badge>
							) : (
								<Badge>
									<Icon name="Check" />
									Instalado
								</Badge>
							)}
							{canManage && (
								<button
									type="button"
									onClick={() =>
										navigate({
											to: "/c/$club/configuracoes/integracoes/checkout/$id",
											params: { club: club.slug, id: integrationId },
										})
									}
								>
									<Badge variant="outline">
										<Icon name="Settings" />
										Gerenciar
									</Badge>
								</button>
							)}
							{canRemove && (
								<button
									type="button"
									onClick={() =>
										setIntegrationToRemove({
											id: integrationId,
											name,
											integrationType,
										})
									}
								>
									<Badge variant="outline" className="text-destructive">
										<Icon name="Trash2" />
										Remover
									</Badge>
								</button>
							)}
						</>
					) : (
						<button type="button" onClick={() => handleIntegrationClick(id)}>
							<Badge variant="outline">
								<Icon name="Plus" />
								Configurar
							</Badge>
						</button>
					)}
				</div>
			</div>
		);
	}

	return (
		<div className="p-6">
			<AlertDialog
				open={!!integrationToRemove}
				onOpenChange={(open) => !open && setIntegrationToRemove(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Remover integração</AlertDialogTitle>
						<AlertDialogDescription>
							Tem certeza que deseja remover a integração{" "}
							<strong>{integrationToRemove?.name}</strong>? Esta ação não pode
							ser desfeita.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isRemovingIntegration}>
							Cancelar
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleRemoveIntegration}
							disabled={isRemovingIntegration}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							{isRemovingIntegration ? "Removendo..." : "Remover"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<Drawer
				open={isGtmDrawerOpen}
				onOpenChange={setIsGtmDrawerOpen}
				direction={isMobile ? "bottom" : "right"}
			>
				<DrawerContent>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							e.stopPropagation();
							gtmForm.handleSubmit();
						}}
					>
						<DrawerHeader>
							<DrawerTitle>Configurar Google Tag Manager</DrawerTitle>
							<DrawerDescription>
								Para adicionar uma tag do Google Tag Manager na sua comunidade,
								precisamos do ID do seu container do GTM. O ID do container é um
								código iniciado com "GTM-" seguido por 8 caracteres e pode ser
								encontrada na lista de tags da sua conta do GTM
							</DrawerDescription>
						</DrawerHeader>

						<div className="p-4 space-y-4">
							<gtmForm.Field name="containerId">
								{(field) => (
									<Field>
										<FieldLabel htmlFor={field.name}>
											ID do Container
										</FieldLabel>
										<Input
											id={field.name}
											name={field.name}
											placeholder="GTM-XXXXXXXX"
											value={field.state.value}
											onBlur={field.handleBlur}
											onChange={(e) => field.handleChange(e.target.value)}
											aria-invalid={
												!!(
													field.state.meta.isTouched &&
													field.state.meta.errors[0]
												)
											}
										/>

										{field.state.meta.isTouched &&
										field.state.meta.errors[0] ? (
											<FieldError errors={field.state.meta.errors} />
										) : null}
									</Field>
								)}
							</gtmForm.Field>
						</div>

						<DrawerFooter>
							<gtmForm.Subscribe selector={(state) => [state.isSubmitting]}>
								{([isSubmitting]) => (
									<>
										<Button
											type="submit"
											disabled={isSubmitting || isCreatingIntegration}
										>
											{isSubmitting || isCreatingIntegration
												? "Salvando..."
												: "Salvar"}
										</Button>
										<DrawerClose asChild>
											<Button variant="outline" type="button">
												Cancelar
											</Button>
										</DrawerClose>
									</>
								)}
							</gtmForm.Subscribe>
						</DrawerFooter>
					</form>
				</DrawerContent>
			</Drawer>

			<Drawer
				open={isGaDrawerOpen}
				onOpenChange={setIsGaDrawerOpen}
				direction={isMobile ? "bottom" : "right"}
			>
				<DrawerContent>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							e.stopPropagation();
							gaForm.handleSubmit();
						}}
					>
						<DrawerHeader>
							<DrawerTitle>Configurar Google Analytics</DrawerTitle>
							<DrawerDescription>
								Para integrar sua conta do Google Analytics na sua comununidade,
								precisamos do ID da propriedade criada no Google Analytics. O ID
								da métrica é um código iniciado com "G-" seguido por 10
								caracteres e pode ser encontrada na página Administradores &gt;
								Coleta e Modificação de dados &gt; Fluxo de Dados
							</DrawerDescription>
						</DrawerHeader>

						<div className="p-4 space-y-4">
							<gaForm.Field name="propertyId">
								{(field) => (
									<Field>
										<FieldLabel htmlFor={field.name}>
											ID da Propriedade
										</FieldLabel>
										<Input
											id={field.name}
											name={field.name}
											placeholder="G-XXXXXXXXXX"
											value={field.state.value}
											onBlur={field.handleBlur}
											onChange={(e) => field.handleChange(e.target.value)}
											aria-invalid={
												!!(
													field.state.meta.isTouched &&
													field.state.meta.errors[0]
												)
											}
										/>

										{field.state.meta.isTouched &&
										field.state.meta.errors[0] ? (
											<FieldError errors={field.state.meta.errors} />
										) : null}
									</Field>
								)}
							</gaForm.Field>
						</div>

						<DrawerFooter>
							<gaForm.Subscribe selector={(state) => [state.isSubmitting]}>
								{([isSubmitting]) => (
									<>
										<Button
											type="submit"
											disabled={isSubmitting || isCreatingIntegration}
										>
											{isSubmitting || isCreatingIntegration
												? "Salvando..."
												: "Salvar"}
										</Button>
										<DrawerClose asChild>
											<Button variant="outline" type="button">
												Cancelar
											</Button>
										</DrawerClose>
									</>
								)}
							</gaForm.Subscribe>
						</DrawerFooter>
					</form>
				</DrawerContent>
			</Drawer>

			<Drawer
				open={isMetaPixelDrawerOpen}
				onOpenChange={setIsMetaPixelDrawerOpen}
				direction={isMobile ? "bottom" : "right"}
			>
				<DrawerContent>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							e.stopPropagation();
							metaPixelForm.handleSubmit();
						}}
					>
						<DrawerHeader>
							<DrawerTitle>Configurar Meta Pixel</DrawerTitle>
							<DrawerDescription>
								Para adicionar o Pixel do Facebook / Meta na sua comunidade,
								precisamos do ID do Pixel que deseja integrar.
							</DrawerDescription>
						</DrawerHeader>

						<div className="p-4 space-y-4">
							<metaPixelForm.Field name="pixelId">
								{(field) => (
									<Field>
										<FieldLabel htmlFor={field.name}>ID do Pixel</FieldLabel>
										<Input
											id={field.name}
											name={field.name}
											placeholder="Digite o ID do Pixel"
											value={field.state.value}
											onBlur={field.handleBlur}
											onChange={(e) => field.handleChange(e.target.value)}
											aria-invalid={
												!!(
													field.state.meta.isTouched &&
													field.state.meta.errors[0]
												)
											}
										/>

										{field.state.meta.isTouched &&
										field.state.meta.errors[0] ? (
											<FieldError errors={field.state.meta.errors} />
										) : null}
									</Field>
								)}
							</metaPixelForm.Field>
						</div>

						<DrawerFooter>
							<metaPixelForm.Subscribe
								selector={(state) => [state.isSubmitting]}
							>
								{([isSubmitting]) => (
									<>
										<Button
											type="submit"
											disabled={isSubmitting || isCreatingIntegration}
										>
											{isSubmitting || isCreatingIntegration
												? "Salvando..."
												: "Salvar"}
										</Button>
										<DrawerClose asChild>
											<Button variant="outline" type="button">
												Cancelar
											</Button>
										</DrawerClose>
									</>
								)}
							</metaPixelForm.Subscribe>
						</DrawerFooter>
					</form>
				</DrawerContent>
			</Drawer>

			<Drawer
				open={isWhatsappDrawerOpen}
				onOpenChange={(open) => {
					setIsWhatsappDrawerOpen(open);
					if (!open) {
						setWhatsappConfirmed(false);
						whatsappForm.reset();
					}
				}}
				direction={isMobile ? "bottom" : "right"}
			>
				<DrawerContent>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							e.stopPropagation();
							whatsappForm.handleSubmit();
						}}
					>
						<DrawerHeader>
							<DrawerTitle>Configurar Whatsapp</DrawerTitle>
							<DrawerDescription asChild>
								<div className="space-y-3">
									<p>
										Para integrar seu grupo do Whatsapp, siga os passos abaixo:
									</p>
									<ol className="list-decimal list-inside space-y-2">
										<li>
											Vá no Whatsapp e adicione no seu grupo o número{" "}
											<strong className="font-mono text-foreground">
												+55 11 5104-0702
											</strong>
										</li>
										<li>
											Copie o exato nome do grupo e cole na caixa de texto
											abaixo
										</li>
									</ol>
								</div>
							</DrawerDescription>
						</DrawerHeader>

						<div className="p-4 space-y-4">
							<whatsappForm.Field name="groupName">
								{(field) => (
									<Field>
										<FieldLabel htmlFor={field.name}>Nome do Grupo</FieldLabel>
										<Input
											id={field.name}
											name={field.name}
											placeholder="Nome exato do grupo no Whatsapp"
											value={field.state.value}
											onBlur={field.handleBlur}
											onChange={(e) => field.handleChange(e.target.value)}
											aria-invalid={
												!!(
													field.state.meta.isTouched &&
													field.state.meta.errors[0]
												)
											}
										/>

										{field.state.meta.isTouched &&
										field.state.meta.errors[0] ? (
											<FieldError errors={field.state.meta.errors} />
										) : null}
									</Field>
								)}
							</whatsappForm.Field>

							<div className="flex items-center gap-2">
								<Checkbox
									id={_whatsappCheckboxId}
									checked={whatsappConfirmed}
									onCheckedChange={(checked) =>
										setWhatsappConfirmed(checked === true)
									}
								/>
								<Label
									htmlFor={_whatsappCheckboxId}
									className="text-sm font-normal"
								>
									Adicionei o telefone no grupo e coloquei o nome do grupo
									exatamente igual.
								</Label>
							</div>
						</div>

						<DrawerFooter>
							<whatsappForm.Subscribe
								selector={(state) => [state.isSubmitting]}
							>
								{([isSubmitting]) => {
									const isBusy = isSubmitting || isCreatingWhatsappIntegration;
									const isDisabled = isBusy || !whatsappConfirmed;

									const button = (
										<Button
											type="submit"
											disabled={isDisabled}
											className="w-full"
										>
											{isBusy ? "Conectando..." : "Conectar"}
										</Button>
									);

									return (
										<>
											{!whatsappConfirmed && !isBusy ? (
												<Tooltip>
													<TooltipTrigger asChild>
														<span className="w-full">{button}</span>
													</TooltipTrigger>
													<TooltipContent>
														Confirme que adicionou o número no grupo
													</TooltipContent>
												</Tooltip>
											) : (
												button
											)}
											<DrawerClose asChild>
												<Button variant="outline" type="button">
													Cancelar
												</Button>
											</DrawerClose>
										</>
									);
								}}
							</whatsappForm.Subscribe>
						</DrawerFooter>
					</form>
				</DrawerContent>
			</Drawer>

			<Tabs
				defaultValue="all"
				className="gap-6"
				value={view}
				onValueChange={(val) =>
					navigate({
						search: (s) => ({
							...s,
							view: val as "all" | "marketing" | "checkout",
						}),
					})
				}
			>
				<div className="flex flex-row items-center justify-between">
					<TabsList>
						<TabsTrigger value="all" className="border-none">
							Todas
						</TabsTrigger>
						<TabsTrigger value="marketing" className="border-none">
							Marketing
						</TabsTrigger>
						<TabsTrigger value="checkout" className="border-none">
							Checkouts
						</TabsTrigger>
					</TabsList>
				</div>

				<TabsContent
					value="all"
					className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
				>
					{allIntegrations.map(renderIntegration)}
				</TabsContent>

				<TabsContent
					value="marketing"
					className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
				>
					{allIntegrations
						.filter((it) => it.type === "MARKETING")
						.map(renderIntegration)}
				</TabsContent>

				<TabsContent
					value="checkout"
					className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
				>
					{allIntegrations
						.filter((it) => it.type === "CHECKOUT")
						.map(renderIntegration)}
				</TabsContent>
			</Tabs>
		</div>
	);
}
